import { useContent } from "@/content/ContentProvider";
import React, { useEffect, useState } from "react";
import { Reveal, Pill } from "./Reveal";
export function Definition({ linkTo = "#journey" }) {
  const { text: cmsText } = useContent();
  return (
    <section
      id="definition"
      className="scroll-mt-10 bg-white px-5 py-20 lg:py-28"
      data-testid="definition"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid gap-10 sm:gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <p className="nm-title text-[84px] leading-[0.9] tracking-[-0.02em] sm:text-[168px]">
              {cmsText("Positioning.Definition.nomus.54c979")}
            </p>
            <p className="mt-4 text-[15px] opacity-50">
              {cmsText("Positioning.Definition.-n-m-s-noun-from-greek.43242f")}{" "}
              <em className="not-italic">
                {cmsText("Positioning.Definition.n-mos.7c3d7e")}
              </em>{" "}
              (νόμος)
            </p>
          </Reveal>
          <div className="space-y-8 lg:col-span-6">
            {[
              [
                "1.",
                cmsText(
                  "Positioning.Definition.custom-norm-the-unwritten-rule-of-how-things-.a1ecf5",
                ),
              ],
              [
                "2.",
                cmsText(
                  "Positioning.Definition.connected-internal-tools-that-make-ai-part-of.08b62b",
                ),
              ],
            ].map(([n, t], i) => (
              <Reveal
                key={n}
                delay={i * 120}
                className="flex gap-5 border-t border-black/10 pt-5"
              >
                <span className="text-[14px] tabular-nums opacity-40">{n}</span>
                <p className="text-[22px] leading-[1.2] tracking-[-0.02em] sm:text-[30px]">
                  {t}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="mt-12 flex flex-col justify-between gap-8 text-[13px] lg:mt-16 lg:flex-row lg:items-end">
          <p className="max-w-[560px] text-[18px] leading-[1.3] tracking-[-0.015em] opacity-70">
            {cmsText(
              "Positioning.Definition.your-software-should-support-the-whole-job-we.cc7293",
            )}
          </p>
          <a href={linkTo} className="nm-link w-fit shrink-0 font-medium">
            {cmsText(
              "Positioning.Definition.follow-the-path-from-novel-to-normal.640218",
            )}
          </a>
        </Reveal>
      </div>
    </section>
  );
}
export function Difference() {
  const { text: cmsText, COMPARE } = useContent();
  return (
    <section
      id="difference"
      className="scroll-mt-10 px-5 pt-28 lg:pt-44"
      data-testid="difference"
    >
      <div className="mx-auto max-w-[1400px]">
        <Pill>{cmsText("Positioning.Difference.the-difference.5abdfa")}</Pill>
        <Reveal className="mt-5">
          <h2 className="nm-title max-w-[900px] text-[34px] leading-[1.02] sm:text-[64px]">
            {cmsText(
              "Positioning.Difference.the-connections-make-the-difference-.0096c1",
            )}
          </h2>
        </Reveal>
        <div className="mt-14">
          <div className="hidden grid-cols-12 gap-4 pb-3 text-[12px] opacity-40 lg:grid">
            <span className="col-span-3">
              {cmsText("Positioning.Difference.the-work.57c12b")}
            </span>
            <span className="col-span-4">
              {cmsText("Positioning.Difference.where-work-gets-stuck.a0e278")}
            </span>
            <span className="col-span-5">
              {cmsText("Positioning.Difference.what-we-build.4f848d")}
            </span>
          </div>
          {COMPARE.map((r, i) => (
            <Reveal
              key={r.who}
              delay={i * 60}
              className="group grid gap-2 border-t border-black/10 py-6 transition-colors duration-500 hover:bg-[#f7f7f7] lg:grid-cols-12 lg:gap-4 lg:px-0"
            >
              <span className="text-[14px] opacity-50 lg:col-span-3 lg:pt-2">
                {r.who}
              </span>
              <span className="text-[22px] tracking-[-0.02em] opacity-40 lg:col-span-4 lg:pt-1 lg:text-[26px]">
                {r.sell}
              </span>
              <span className="text-[26px] leading-[1.1] tracking-[-0.03em] transition-transform duration-500 group-hover:translate-x-2 lg:col-span-5 lg:text-[34px]">
                {r.we}
              </span>
            </Reveal>
          ))}
          <div className="border-t border-black/10" />
        </div>
      </div>
    </section>
  );
}
const RAIL = [
  ["hero", "Intro"],
  ["manifesto", "Why"],
  ["commitments", "What you get"],
  ["monday", "A Monday"],
  ["definition", "Definition"],
  ["journey", "Journey"],
  ["difference", "Difference"],
  ["method", "Method"],
  ["audiences", "For you"],
  ["contact", "Talk"],
];
export function ChapterRail() {
  const { text: cmsText } = useContent();
  const [active, setActive] = useState(0);
  const [present, setPresent] = useState(RAIL);
  useEffect(() => {
    const els = RAIL.map(([id]) => document.getElementById(id)).filter(Boolean);
    setPresent(RAIL.filter(([id]) => document.getElementById(id)));
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting)
            setActive(RAIL.findIndex(([id]) => id === e.target.id));
        }),
      {
        rootMargin: "-45% 0px -50% 0px",
      },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <nav
      aria-label={cmsText("Positioning.ChapterRail.chapters.034e27")}
      data-testid="chapter-rail"
      className="group/rail pointer-events-none fixed right-1 top-[60%] z-[46] hidden -translate-y-1/2 text-white mix-blend-difference xl:block"
    >
      <ul className="flex flex-col items-end gap-3">
        {present.map(([id, label]) => {
          const i = RAIL.findIndex(([x]) => x === id);
          return (
            <li key={id} className="pointer-events-auto">
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(id)?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
                className="group flex items-center gap-3 text-[11px] font-medium"
                aria-current={active === i ? "true" : undefined}
              >
                <span
                  className={`transition-all duration-500 ${active === i ? "translate-x-2 opacity-0 group-hover/rail:translate-x-0 group-hover/rail:opacity-100" : "translate-x-2 opacity-0 group-hover/rail:translate-x-0 group-hover/rail:opacity-60"}`}
                >
                  {label}
                </span>
                <span
                  className={`block h-px bg-current transition-all duration-500 ${active === i ? "w-4" : "w-2 opacity-60 group-hover:w-4"}`}
                />
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
